import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Stethoscope,
  Info,
  Phone,
  AlertTriangle,
  BookOpen,
  ThumbsUp,
  ThumbsDown,
  MessageCirclePlus,
  ExternalLink,
  StickyNote,
  Share2,
  Copy,
  Check,
  UserPlus,
  Send,
  Loader2,
  Printer,
  Compass,
  ArrowRight,
  Wand2,
  CreditCard,
  ImagePlus,
  FileText,
  Languages,
  X,
} from "lucide-react";
import { EmergencyBanner } from "./EmergencyBanner";
import { AudioPlayer } from "./AudioPlayer";
import { QuestionBox } from "./QuestionBox";
import { sendFeedback, createShare, createDoctorRequest, simplifyAnswer, createReviewCheckout, uploadPhoto, recordUpsellClick, getUpsellConfig, getLearnIndex } from "../lib/api";
import { getRelated } from "../lib/related";
import { addReview } from "../lib/reviews";
import { ReviewHistory } from "./ReviewHistory";
import { matchTool } from "../lib/toolMatch";
import { InlineToolCard } from "./tools/calculators";
import { EmailSummaryForm } from "./EmailSummary";
import { useT, useLang, tr, LANG_LABELS } from "../lib/i18n";

function getUpsellVariant() {
  try {
    let v = localStorage.getItem("amd-upsell-variant");
    if (v !== "A" && v !== "B") {
      v = Math.random() < 0.5 ? "A" : "B";
      localStorage.setItem("amd-upsell-variant", v);
    }
    return v;
  } catch {
    return "A";
  }
}

const Section = ({ icon: Icon, title, children, tone = "default", testId }) => {
  const tones = {
    default: "bg-white border-slate-200",
    teal: "bg-teal-50/60 border-teal-200",
    amber: "bg-amber-50 border-amber-300",
    red: "bg-red-50 border-red-300",
  };
  const iconTones = {
    default: "text-slate-500",
    teal: "text-teal-600",
    amber: "text-amber-600",
    red: "text-red-600",
  };
  return (
    <div className={`rounded-2xl border-2 p-5 sm:p-6 ${tones[tone]}`} data-testid={testId}>
      <h3 className="font-heading font-bold text-xl sm:text-2xl text-slate-900 flex items-center gap-2.5 mb-3">
        <Icon className={`w-6 h-6 ${iconTones[tone]}`} /> {title}
      </h3>
      <div className="text-lg text-slate-800 leading-relaxed whitespace-pre-line">
        {children}
      </div>
    </div>
  );
};

const LAB_KEYWORDS = [
  "lab", "labs", "result", "results", "blood work", "bloodwork", "blood test",
  "a1c", "cholesterol level", "my level", "my reading", "my values", "panel",
  "test came back", "my report", "cbc", "lipid", "creatinine", "tsh", "psa level",
];

const isLabQuestion = (q) => {
  const t = (q || "").toLowerCase();
  return LAB_KEYWORDS.some((k) => t.includes(k));
};

export const AnswerDisplay = ({ result, onFollowUp, done = true, shareEnabled = true, currentSlug = null, reviewLookup = false, autoOpenReview = false }) => {
  const t = useT();
  const { lang } = useLang();
  const secLang = result.answer_lang || lang;
  const st = (k) => tr(secLang, k);
  const [feedback, setFeedback] = useState(null);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDoctor, setShowDoctor] = useState(false);
  const [doctorContact, setDoctorContact] = useState("");
  const [doctorSending, setDoctorSending] = useState(false);
  const [doctorSent, setDoctorSent] = useState(false);
  const [simpler, setSimpler] = useState("");
  const [simplifying, setSimplifying] = useState(false);
  const [paying, setPaying] = useState(false);
  const labSuggested = isLabQuestion(result.question);
  const [payTier, setPayTier] = useState(labSuggested ? "lab_review" : "reviewed_answer");
  const [upsellVariant, setUpsellVariant] = useState(getUpsellVariant);
  const [fromUpsell, setFromUpsell] = useState(false);

  useEffect(() => {
    getUpsellConfig().then((cfg) => {
      if (cfg && cfg.locked && (cfg.variant === "A" || cfg.variant === "B")) {
        setUpsellVariant(cfg.variant);
      }
    });
  }, []);
  const [labFiles, setLabFiles] = useState([]);
  const labFileRef = useRef(null);
  const a = result.answer || {};
  const autoReviewFired = useRef(false);
  const inlineToolSlug = done && a.simple_answer ? matchTool(result.question) : null;

  useEffect(() => {
    if (autoOpenReview && done && a.simple_answer && !autoReviewFired.current) {
      autoReviewFired.current = true;
      setShowDoctor(true);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="ask-doctor-box"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenReview, done, a.simple_answer]);

  const related =
    done && a.simple_answer
      ? getRelated(result.question, result.category_label, currentSlug, 3)
      : [];

  // Localize Related Reading link titles to the answer language (from the library index).
  const [relatedTitles, setRelatedTitles] = useState({});
  useEffect(() => {
    if (secLang && secLang !== "en" && related.length) {
      getLearnIndex(secLang)
        .then((pages) => {
          const map = {};
          for (const p of pages) map[p.slug] = p.title;
          setRelatedTitles(map);
        })
        .catch(() => {});
    } else {
      setRelatedTitles({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secLang, related.length]);

  const ttsText = [
    a.simple_answer,
    a.what_it_means && `${st("sec_what")}. ${a.what_it_means}`,
    a.when_to_contact_doctor && `${st("sec_contact")}. ${a.when_to_contact_doctor}`,
    a.get_urgent_help && `${st("sec_urgent")} ${a.get_urgent_help}`,
  ]
    .filter(Boolean)
    .join(". ");

  const submitFeedback = async (helpful) => {
    setFeedback(helpful);
    if (result.answer_id) {
      try {
        await sendFeedback(result.answer_id, helpful);
      } catch (e) {
        console.error("feedback failed", e);
      }
    }
  };

  const handleShare = async () => {
    if (shareUrl) return;
    setSharing(true);
    try {
      const { share_id } = await createShare({
        question: result.question,
        category: result.category,
        category_label: result.category_label,
        answer: a,
        sources: result.sources,
        emergency: result.emergency,
      });
      setShareUrl(`${window.location.origin}/s/${share_id}`);
    } catch (e) {
      console.error("share link failed", e);
    } finally {
      setSharing(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("copy link failed", e);
    }
  };

  const submitDoctor = async () => {
    setDoctorSending(true);
    try {
      const res = await createDoctorRequest({
        answer_id: result.answer_id,
        question: result.question,
        contact: doctorContact,
      });
      if (res && res.request_id) {
        addReview({ request_id: res.request_id, question: result.question });
      }
      setDoctorSent(true);
    } catch (e) {
      console.error("doctor request failed", e);
    } finally {
      setDoctorSending(false);
    }
  };

  const handleSimplify = async () => {
    if (simplifying) return;
    setSimplifying(true);
    try {
      const t = await simplifyAnswer(ttsText, secLang);
      setSimpler(t);
    } catch (e) {
      console.error("simplify failed", e);
    } finally {
      setSimplifying(false);
    }
  };

  const pickLabFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const next = [...labFiles];
    for (const f of files) {
      if (next.length >= 3) break;
      const okType = /^image\//.test(f.type) || f.type === "application/pdf";
      if (!okType) continue;
      if (f.size > 10 * 1024 * 1024) continue;
      next.push(f);
    }
    setLabFiles(next);
    if (labFileRef.current) labFileRef.current.value = "";
  };

  const handlePayReview = async () => {
    if (paying) return;
    setPaying(true);
    try {
      let attachments = [];
      if (payTier === "lab_review" && labFiles.length) {
        attachments = await Promise.all(labFiles.map((f) => uploadPhoto(f)));
      }
      const { checkout_url } = await createReviewCheckout({
        origin_url: window.location.origin,
        question: result.question,
        contact: doctorContact,
        answer_id: result.answer_id,
        tier: payTier,
        attachments,
        upsell_variant: fromUpsell ? upsellVariant : undefined,
      });
      if (checkout_url) window.location.href = checkout_url;
    } catch (e) {
      setPaying(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up answer-print">
      {/* Print-only header (hidden on screen, shown when printing) */}
      <div className="print-only" style={{ display: "none" }} data-testid="print-header">
        <div style={{ borderBottom: "2px solid #0d9488", paddingBottom: "10px", marginBottom: "16px" }}>
          <div style={{ fontSize: "20px", fontWeight: 800, color: "#0d9488" }}>
            AskMyDoctor AI
          </div>
          <div style={{ fontSize: "16px", fontWeight: 700, marginTop: "8px", color: "#000" }}>
            Question: {result.question}
          </div>
          <div style={{ fontSize: "12px", color: "#333", marginTop: "4px" }}>
            Printed {new Date().toLocaleString()}
          </div>
          <div style={{ fontSize: "12px", color: "#333", marginTop: "6px", fontStyle: "italic" }}>
            This is general medical information and education — not medical advice, a
            diagnosis, or a substitute for a doctor. In an emergency, call 911.
          </div>
        </div>
      </div>

      <EmergencyBanner emergency={result.emergency} />

      {done && result.answer_lang && result.answer_lang !== lang && (
        <div
          className="inline-flex items-center gap-2 text-sm font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-3.5 py-1.5"
          data-testid="answer-lang-chip"
          data-no-print
        >
          <Languages className="w-4 h-4" /> {st("answered_in")} {LANG_LABELS[secLang]}
        </div>
      )}

      {reviewLookup && done && result.question && (
        <ReviewHistory question={result.question} />
      )}

      {done && a.simple_answer && (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-start" data-no-print>
          <div className="flex-1">
            <AudioPlayer text={ttsText} lang={secLang} />
          </div>
          <div className="flex flex-col gap-2 sm:w-56 shrink-0">
            <button
              onClick={() => window.print()}
              data-testid="print-answer-button"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-50 font-bold text-lg px-5 py-3 rounded-xl transition-transform active:scale-95 min-h-[52px]"
            >
              <Printer className="w-5 h-5" /> {st("print_save")}
            </button>
            <button
              onClick={handleSimplify}
              disabled={simplifying}
              data-testid="simplify-button"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-teal-500 text-teal-700 hover:bg-teal-50 disabled:opacity-60 font-bold text-lg px-5 py-3 rounded-xl transition-transform active:scale-95 min-h-[52px]"
            >
              {simplifying ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}
              {simplifying ? st("simplifying") : st("simplify")}
            </button>
          </div>
        </div>
      )}

      {simpler && (
        <div className="rounded-2xl border-2 border-teal-300 bg-teal-50/60 p-5 sm:p-6" data-testid="simpler-answer">
          <h3 className="font-heading font-bold text-xl sm:text-2xl text-slate-900 flex items-center gap-2.5 mb-3">
            <Wand2 className="w-6 h-6 text-teal-600" /> {st("simpler_heading")}
          </h3>
          <div className="text-lg text-slate-800 leading-relaxed whitespace-pre-line mb-3">
            {simpler}
          </div>
          <div data-no-print>
            <AudioPlayer text={simpler} lang={secLang} />
          </div>
        </div>
      )}

      {a.simple_answer && (
        <Section icon={Stethoscope} title={st("sec_simple")} tone="teal" testId="answer-simple">
          {a.simple_answer}
        </Section>
      )}

      {a.what_it_means && (
        <Section icon={Info} title={st("sec_what")} testId="answer-what-it-means">
          {a.what_it_means}
        </Section>
      )}

      {a.when_to_contact_doctor && (
        <Section
          icon={Phone}
          title={st("sec_contact")}
          tone="amber"
          testId="answer-contact-doctor"
        >
          {a.when_to_contact_doctor}
        </Section>
      )}

      {a.get_urgent_help && (
        <Section
          icon={AlertTriangle}
          title={st("sec_urgent")}
          tone="red"
          testId="answer-urgent-help"
        >
          {a.get_urgent_help}
        </Section>
      )}

      {a.note && (
        <div className="rounded-xl bg-slate-100 border border-slate-200 p-4 flex items-start gap-3 text-base text-slate-700" data-testid="answer-note">
          <StickyNote className="w-5 h-5 shrink-0 mt-0.5 text-slate-500" />
          <p className="leading-relaxed">{a.note}</p>
        </div>
      )}

      {/* Sources */}
      {result.sources && result.sources.length > 0 && (
        <div className="rounded-2xl border-2 border-slate-200 bg-white p-5 sm:p-6" data-testid="answer-sources">
          <h3 className="font-heading font-bold text-xl text-slate-900 flex items-center gap-2.5 mb-3">
            <BookOpen className="w-6 h-6 text-slate-500" /> {st("sources")}
          </h3>
          <p className="text-base text-slate-600 mb-3">
            {st("sources_intro")}
          </p>
          <ul className="space-y-2">
            {result.sources.map((s, i) => (
              <li key={s.source_url || i}>
                <a
                  href={s.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`source-link-${i}`}
                  className="inline-flex items-center gap-1.5 text-teal-700 hover:text-teal-800 font-semibold text-lg underline decoration-teal-300 underline-offset-2"
                >
                  {s.source_name}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Relevant inline calculator */}
      {inlineToolSlug && (
        <InlineToolCard slug={inlineToolSlug} />
      )}

      {/* Gentle follow-up upsell (non-lab questions) */}
      {done && !labSuggested && !doctorSent && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border-2 border-sky-200 bg-sky-50/60 p-5" data-testid="reviewed-upsell" data-no-print>
          <Stethoscope className="w-6 h-6 text-sky-600 shrink-0" />
          <p className="text-base text-slate-700 flex-1" data-testid="reviewed-upsell-copy">
            {t(upsellVariant === "B" ? "upsell_b" : "upsell_a")}
          </p>
          <button
            onClick={() => {
              setPayTier("reviewed_answer");
              setFromUpsell(true);
              recordUpsellClick(upsellVariant);
              setShowDoctor(true);
              setTimeout(() => {
                const el = document.querySelector('[data-testid="ask-doctor-box"]');
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
              }, 60);
            }}
            data-testid="reviewed-upsell-button"
            className="inline-flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold text-base px-5 py-2.5 rounded-xl transition-transform active:scale-95 shrink-0"
          >
            {t("upsell_cta")}
          </button>
        </div>
      )}

      {/* Related Reading */}
      {related.length > 0 && (
        <div className="rounded-2xl border-2 border-teal-200 bg-teal-50/40 p-5 sm:p-6" data-testid="related-reading" data-no-print>
          <h3 className="font-heading font-bold text-xl text-slate-900 flex items-center gap-2.5 mb-3">
            <Compass className="w-6 h-6 text-teal-600" /> {st("related_reading")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {related.map((r, i) => (
              <Link
                key={r.slug}
                to={secLang && secLang !== "en" ? `/${secLang}/${r.slug}` : `/${r.slug}`}
                data-testid={`related-link-${i}`}
                className="group flex items-center justify-between gap-2 bg-white border-2 border-slate-200 hover:border-teal-400 rounded-xl px-4 py-3 transition-all min-h-[52px]"
              >
                <span className="font-semibold text-base text-slate-800">{relatedTitles[r.slug] || r.title}</span>
                <ArrowRight className="w-4 h-4 text-teal-500 shrink-0 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Feedback */}
      {done && a.simple_answer && (
        <div className="rounded-2xl border-2 border-slate-200 bg-white p-5 sm:p-6" data-testid="feedback-box" data-no-print>
          <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
            {t("helpful_q")}
          </h3>
          {feedback === null ? (
            <div className="flex gap-3">
              <button
                onClick={() => submitFeedback(true)}
                data-testid="thumbs-up-button"
                className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold text-lg px-6 py-3 rounded-xl transition-transform active:scale-95 min-h-[52px]"
              >
                <ThumbsUp className="w-5 h-5" /> {t("yes")}
              </button>
              <button
                onClick={() => submitFeedback(false)}
                data-testid="thumbs-down-button"
                className="inline-flex items-center gap-2 bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-50 font-bold text-lg px-6 py-3 rounded-xl transition-transform active:scale-95 min-h-[52px]"
              >
                <ThumbsDown className="w-5 h-5" /> {t("no")}
              </button>
            </div>
          ) : (
            <p className="text-lg text-teal-700 font-semibold" data-testid="feedback-thanks">
              {t("feedback_thanks")}
            </p>
          )}
        </div>
      )}

      {/* Streaming indicator */}
      {!done && a.simple_answer && (
        <div
          className="flex items-center gap-2 text-base text-teal-700 font-medium"
          data-testid="streaming-indicator"
          data-no-print
        >
          <Loader2 className="w-4 h-4 animate-spin" /> {t("writing_answer")}
        </div>
      )}

      {/* Follow-up */}
      {done && onFollowUp && (
        <div data-testid="followup-section" data-no-print>
          {!showFollowUp ? (
            <button
              onClick={() => setShowFollowUp(true)}
              data-testid="ask-followup-button"
              className="inline-flex items-center gap-2 bg-white border-2 border-teal-500 text-teal-700 hover:bg-teal-50 font-bold text-lg px-6 py-3 rounded-xl transition-transform active:scale-95 min-h-[52px]"
            >
              <MessageCirclePlus className="w-5 h-5" /> {t("ask_followup")}
            </button>
          ) : (
            <div className="rounded-2xl border-2 border-teal-200 bg-teal-50/40 p-5">
              <h3 className="font-heading font-bold text-lg text-slate-900 mb-3">
                {t("followup_ask_heading")}
              </h3>
              <QuestionBox
                testIdPrefix="followup"
                placeholder={t("followup_ask_ph")}
                buttonLabel={t("followup_send")}
                onSubmit={(q) => {
                  setShowFollowUp(false);
                  onFollowUp(q);
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Save & Share */}
      {done && shareEnabled && a.simple_answer && (
        <div className="rounded-2xl border-2 border-slate-200 bg-white p-5 sm:p-6" data-testid="share-box" data-no-print>
          <h3 className="font-heading font-bold text-xl text-slate-900 flex items-center gap-2.5 mb-3">
            <Share2 className="w-6 h-6 text-teal-600" /> {t("share_title")}
          </h3>
          {!shareUrl ? (
            <>
              <p className="text-base text-slate-600 mb-3">
                {t("share_desc")}
              </p>
              <button
                onClick={handleShare}
                disabled={sharing}
                data-testid="save-share-button"
                className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-bold text-lg px-6 py-3 rounded-xl transition-transform active:scale-95 min-h-[52px]"
              >
                {sharing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Share2 className="w-5 h-5" />}
                {sharing ? t("share_creating") : t("share_create")}
              </button>
            </>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                readOnly
                value={shareUrl}
                data-testid="share-link-input"
                onFocus={(e) => e.target.select()}
                className="flex-1 bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3 text-base text-slate-700 min-h-[52px]"
              />
              <button
                onClick={copyLink}
                data-testid="copy-share-button"
                className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold text-lg px-6 py-3 rounded-xl transition-transform active:scale-95 min-h-[52px]"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                {copied ? t("share_copied") : t("share_copy")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Email this answer */}
      {done && a.simple_answer && (
        <EmailSummaryForm variant="answer" title={result.question} summary={ttsText} source="answer" />
      )}

      {/* Ask a Doctor (optional future hand-off) */}
      {done && a.simple_answer && (
        <div className="rounded-2xl border-2 border-sky-200 bg-sky-50/60 p-5 sm:p-6" data-testid="ask-doctor-box" data-no-print>
          <h3 className="font-heading font-bold text-xl text-slate-900 flex items-center gap-2.5 mb-2">
            <UserPlus className="w-6 h-6 text-sky-600" /> {t("ask_doctor")}
          </h3>
          {!doctorSent ? (
            <>
              <p className="text-base text-slate-600 mb-3">
                {t("doctor_desc")}
              </p>
              {!showDoctor ? (
                <button
                  onClick={() => setShowDoctor(true)}
                  data-testid="ask-doctor-button"
                  className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold text-lg px-6 py-3 rounded-xl transition-transform active:scale-95 min-h-[52px]"
                >
                  <UserPlus className="w-5 h-5" /> {t("doctor_get_reviewed")}
                </button>
              ) : (
                <div className="space-y-3">
                  {labSuggested && (
                    <div className="flex items-start gap-2.5 rounded-xl border-2 border-violet-200 bg-violet-50 p-3.5" data-testid="lab-upgrade-nudge">
                      <FileText className="w-5 h-5 text-violet-600 mt-0.5 shrink-0" />
                      <p className="text-base text-violet-900">
                        {t("lab_nudge")}
                      </p>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-3">
                    <button
                      onClick={() => setPayTier("reviewed_answer")}
                      data-testid="tier-reviewed"
                      className={`text-left rounded-xl border-2 p-4 transition-colors ${
                        payTier === "reviewed_answer"
                          ? "border-sky-500 bg-sky-100"
                          : "border-slate-200 bg-white hover:border-sky-300"
                      }`}
                    >
                      <div className="font-bold text-slate-900 text-lg">{t("tier_reviewed_title")}</div>
                      <div className="text-sm text-slate-600 mt-0.5">{t("tier_reviewed_desc")}</div>
                    </button>
                    <button
                      onClick={() => setPayTier("lab_review")}
                      data-testid="tier-lab"
                      className={`text-left rounded-xl border-2 p-4 transition-colors ${
                        payTier === "lab_review"
                          ? "border-sky-500 bg-sky-100"
                          : "border-slate-200 bg-white hover:border-sky-300"
                      }`}
                    >
                      <div className="font-bold text-slate-900 text-lg">{t("tier_lab_title")}</div>
                      <div className="text-sm text-slate-600 mt-0.5">{t("tier_lab_desc")}</div>
                    </button>
                  </div>

                  <input
                    value={doctorContact}
                    onChange={(e) => setDoctorContact(e.target.value)}
                    placeholder={t("doctor_contact_ph")}
                    data-testid="doctor-contact-input"
                    className="w-full bg-white border-2 border-sky-200 rounded-xl px-4 py-3 text-base text-slate-700 min-h-[52px]"
                  />

                  {payTier === "lab_review" && (
                    <div className="rounded-xl border-2 border-dashed border-sky-300 bg-white p-4" data-testid="lab-upload">
                      <p className="text-sm font-semibold text-slate-700 mb-2">
                        {t("lab_attach_label")}
                      </p>
                      {labFiles.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {labFiles.map((f, k) => (
                            <div key={`${f.name}-${f.size}`} className="flex items-center gap-1.5 bg-slate-100 rounded-lg pl-2 pr-1 py-1 text-sm text-slate-700">
                              <FileText className="w-4 h-4 text-slate-500" />
                              <span className="truncate max-w-[140px]">{f.name}</span>
                              <button onClick={() => setLabFiles((p) => p.filter((_, i) => i !== k))} className="text-slate-400 hover:text-slate-700">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <input ref={labFileRef} type="file" accept="image/*,application/pdf" multiple onChange={pickLabFiles} data-testid="lab-file-input" className="hidden" />
                      <button
                        onClick={() => labFileRef.current && labFileRef.current.click()}
                        disabled={labFiles.length >= 3}
                        data-testid="lab-add-file"
                        className="inline-flex items-center gap-2 border-2 border-sky-200 hover:border-sky-400 disabled:opacity-50 text-sky-700 font-semibold text-base px-4 py-2 rounded-lg transition-colors"
                      >
                        <ImagePlus className="w-5 h-5" /> {t("lab_add_file")}{labFiles.length ? ` (${labFiles.length}/3)` : ""}
                      </button>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handlePayReview}
                      disabled={paying}
                      data-testid="pay-review-button"
                      className="inline-flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white font-bold text-lg px-6 py-3 rounded-xl transition-transform active:scale-95 min-h-[52px]"
                    >
                      {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                      {paying ? t("pay_redirecting") : payTier === "lab_review" ? t("pay_20") : t("pay_10")}
                    </button>
                    <button
                      onClick={submitDoctor}
                      disabled={doctorSending}
                      data-testid="doctor-submit-button"
                      className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-60 font-semibold text-base px-5 py-3 rounded-xl transition-transform active:scale-95 min-h-[52px]"
                    >
                      {doctorSending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                      {t("doctor_register_free")}
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">
                    {t("stripe_note")}
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="text-lg text-sky-700 font-semibold" data-testid="doctor-sent">
              {t("doctor_sent_msg")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
